import { AccessToken, TokenCredential } from '@azure/core-auth';
import { useAccount, useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthSession } from '~/contexts/auth/AuthSessionContext';
import {
    isEntraAuthEnabled,
    isBrokerAuthEnabled,
    requiresEntraResourceToken,
} from '~/lib/auth';
import { getStoredUser } from '~/lib/oidc';
import AppConfig from '~/utils/appconfig';
import { handleError } from '~/utils/errors';
import { checkIsInSessionRouteWithoutRouter } from '../session/useIsInSessionRoute';

type AccessTokenOptions = {
    scopes: string[];
};

export interface TokenProvider extends TokenCredential {
    getToken: () => Promise<AccessToken | null>;
}

const NO_TOKEN: AccessToken = { token: '', expiresOnTimestamp: 0 };

/**
 * Hook that provides an access-token provider for the signed-in user.
 *
 * The scopes decide which identity provider serves the request. Azure resource
 * scopes are minted by Entra alone, so they always go to MSAL; everything else
 * comes from whichever provider the application is signed in against. This is
 * what lets Blob storage keep working on the Entra path while the application
 * itself authenticates against the broker.
 *
 * @param {AccessTokenOptions} options - Options for the retrieved access token.
 *   `scopes` - An array of strings representing the scopes for which the token is requested.
 * @returns An object containing the token provider and authentication status.
 *   `tokenProvider` - The token provider for Azure services.
 *   `isAuthenticated` - Whether the user is authenticated.
 */
export const useAccessTokenProvider = ({ scopes }: AccessTokenOptions) => {
    const isInConnectorRoute = checkIsInSessionRouteWithoutRouter();
    const { isAuthenticated: isSessionAuthenticated } = useAuthSession();
    const isAuthenticated = isSessionAuthenticated || isInConnectorRoute;
    const { instance } = useMsal();
    const account = useAccount();

    // Resolved at render time so the routing decision cannot change between
    // acquiring a token and using it.
    const needsEntraToken = useMemo(
        () => requiresEntraResourceToken(scopes, AppConfig.clientId),
        [scopes]
    );
    const useBrokerToken = isBrokerAuthEnabled() && !needsEntraToken;

    const acquireToken = useCallback(async (): Promise<AccessToken> => {
        if (isInConnectorRoute) {
            return NO_TOKEN;
        }

        if (useBrokerToken) {
            try {
                const user = await getStoredUser();
                if (user === null) {
                    throw new Error(
                        'No broker session. User may need to sign in.'
                    );
                }
                return {
                    token: user.access_token,
                    // oidc-client-ts reports expiry in seconds since epoch.
                    expiresOnTimestamp: (user.expires_at ?? 0) * 1000,
                };
            } catch (error) {
                handleError(
                    error,
                    'Token acquisition failed. User may need to sign in again.'
                );
                return NO_TOKEN;
            }
        }

        if (!isEntraAuthEnabled()) {
            return NO_TOKEN;
        }

        try {
            if (!account) {
                throw new Error(
                    'No active account set. User may need to sign in.'
                );
            }

            const tokenResult = await instance.acquireTokenSilent({
                scopes,
                account,
            });

            return {
                token: tokenResult.accessToken,
                expiresOnTimestamp: tokenResult.expiresOn?.getTime() || 0,
            };
        } catch (error) {
            handleError(
                error,
                'Silent token acquisition failed. User may need to sign in again.'
            );
            return NO_TOKEN;
        }
    }, [instance, scopes, isInConnectorRoute, account, useBrokerToken]);

    // getToken must always invoke the *latest* closure (current account and
    // scopes) while the tokenProvider object identity stays stable — backend
    // clients are memoized on it. A ref bridges the two; a directly captured
    // closure would permanently use the first render's account, acquiring
    // tokens for a stale identity after an account switch.
    const acquireTokenRef = useRef(acquireToken);
    useEffect(() => {
        acquireTokenRef.current = acquireToken;
    }, [acquireToken]);

    const [tokenProvider] = useState<TokenProvider>(() => ({
        async getToken(): Promise<AccessToken | null> {
            return acquireTokenRef.current();
        },
    }));

    return { tokenProvider, isAuthenticated };
};
